import { createContext, type ReactNode, useContext, useEffect, useState } from 'react';

export type Theme = 'light' | 'dark';
export type ThemePreference = Theme | 'auto';

export interface ThemeContextValue {
    theme: Theme;
    isAutoTheme: boolean;
    toggleTheme: (theme: ThemePreference) => void;
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

const getSystemTheme = (): Theme => (window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light');

const useSystemTheme = () => {
    const [systemTheme, setSystemTheme] = useState<Theme>(getSystemTheme);

    useEffect(() => {
        const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
        const handleChange = (event: MediaQueryListEvent) => {
            setSystemTheme(event.matches ? 'dark' : 'light');
        };

        mediaQuery.addEventListener('change', handleChange);
        return () => mediaQuery.removeEventListener('change', handleChange);
    }, []);

    return systemTheme;
};

export const useTheme = () => {
    const context = useContext(ThemeContext);

    if (!context) {
        throw new Error('useTheme must be used within a ThemeProvider');
    }

    return context;
};

export const ThemeProvider = ({ children }: { children: ReactNode }) => {
    const systemTheme = useSystemTheme();
    const [theme, setTheme] = useState<Theme>(() => {
        const savedTheme = globalThis.localStorage?.getItem('theme');
        return savedTheme === 'light' || savedTheme === 'dark' ? savedTheme : getSystemTheme();
    });
    const [isAutoTheme, setIsAutoTheme] = useState(
        () => globalThis.localStorage?.getItem('AutoThemeMode') === 'true',
    );
    const resolvedTheme = isAutoTheme ? systemTheme : theme;

    const toggleTheme = (nextTheme: ThemePreference) => {
        const isAuto = nextTheme === 'auto';

        setIsAutoTheme(isAuto);
        globalThis.localStorage?.setItem('AutoThemeMode', String(isAuto));

        if (!isAuto) {
            setTheme(nextTheme);
            globalThis.localStorage?.setItem('theme', nextTheme);
        }
    };

    useEffect(() => {
        document.documentElement.setAttribute('data-theme', resolvedTheme);
    }, [resolvedTheme]);

    return (
        <ThemeContext.Provider value={{ theme: resolvedTheme, isAutoTheme, toggleTheme }}>
            {children}
        </ThemeContext.Provider>
    );
};
